import { Alert, Linking } from 'react-native';
import React, { useEffect, useMemo, useState } from 'react';
import { AuthInput } from '../../../Interface/Forms/Inputs/AuthInput';
import { BirthInput } from '../../../Interface/Forms/Inputs/BirthInput';
import {
  Form,
  FormControl,
  Label,
  Subtitle,
  Title,
  Step,
  CircleStep,
  LineStep,
  StepText,
  LineStepSeparator,
  FormStep,
  IconButton,
  TermCheckButton,
  TermCheckIcon,
  TermContainer,
  TermLinkButton,
  TermLinkText,
  Footer,
  BackgroundHeader,
  TermContainerBox,
  PromoCheckButton,
  PromoCheckIcon,
  PromosContainer,
  PromoLabel,
  FormHeader,
  FormInfos,
} from '../styles';
import { api } from '../../../../services/api';
import { cpf as cpfValidator } from 'cpf-cnpj-validator';
import { Button } from '../../../Interface/Forms/Buttons/Button';
import { IconHeader } from '../../../Interface/Headers/Header/styles';
import { useAuth } from '../../../../context/auth';

import { getInstallAttributionData } from '../../../../utils/installAttribution';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export default function SignupForm({ onBack, onSuccess }: Props) {
  const { stringToHex, signIn, setShowSign, setShouldShowOTPModal, clientId } = useAuth();

  const [cpf, setCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [cpfIsValid, setCpfIsValid] = useState(false);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [aceitaReceberPromos, setAceitaReceberPromos] = useState(false);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');

  const [birth, setBirth] = useState('');
  const [birthEnabled, setBirthEnabled] = useState(false);

  const [phone, setPhone] = useState('');
  const [phoneRaw, setPhoneRaw] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const [formStep, setFormStep] = useState<'info' | 'auth' | 'indica'>('info');
  const [termAccepted, setTermAccepted] = useState(false);

  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);
  const [isLoadingIndica, setIsLoadingIndica] = useState(false);

  const isInfoEnabled = useMemo(
    () =>
      phoneIsValid &&
      name.length >= 5 &&
      birthEnabled &&
      !nameError,
    [phoneIsValid, name, birthEnabled, nameError],
  );

  const isAuthEnabled = useMemo(
    () => cpfIsValid && password.length >= 4,
    [cpfIsValid, password],
  );

  const isIndicaEnabled = useMemo(() => termAccepted, [termAccepted]);

  function handleNameChangeText(e: string) {
    // verifica se tem caractere inválido
    const hasInvalidChar = /[^A-Za-zÀ-ÖØ-öø-ÿ\s]/.test(e);

    if (hasInvalidChar) {
      setNameError('O nome não pode conter números ou caracteres especiais');
    } else {
      setNameError('');
    }

    const filteredName = e
      .replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/^\s/, '');

    setName(filteredName);
  }

  function handleEmailChangeText(e: string) {
    setEmail(e);
    setEmailError('');
    setEmailIsValid(false);
  }

  function handleCpfChangeText(e: string) {
    setCpfError('');
    setCpf(e);
    setCpfIsValid(false);
  }

  function handlePhoneChangeText(e: string, raw?: string) {
    if (e === phone) {
      return;
    }

    setPhone(e);
    setPhoneRaw((raw ?? e).replace(/\D/g, ''));
    setPhoneError('');
    setPhoneIsValid(false);
  }

  function handlePasswordChangeText(e: string) {
    setPassword(e);
    setPasswordError('');
  }

  function handlCodeChangeText(e: string) {
    setCode(e);
    setCodeError('');
  }

  async function validateEmail(): Promise<boolean> {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    if (isValid) {
      try {
        await api.post('/verifyEmailExists', {
          email,
        });
        setEmailIsValid(true);
        return true;
      } catch (error: any) {
        const err = error?.response?.data?.error;
        if (err) {
          setEmailError('E-mail já cadastrado');
        }
        return false;
      }
    } else {
      setEmailError('E-mail inválido');
    }
    return false;
  }

  async function validateCpf() {
    const isValid = cpfValidator.isValid(cpf);
    if (!isValid) {
      return setCpfError('CPF Inválido');
    }
    try {
      await api.post('/verifyCpfExists', {
        cpf,
      });
      setCpfIsValid(true);
    } catch (error: any) {
      const err = error?.response?.data?.error;
      if (err) {
        setCpfError(err);
      }
    }
  }

  async function validatePhone() {
    setIsLoadingInfo(true);
    try {
      await api.post('/verifyPhoneExists', {
        phone,
      });
      setPhoneIsValid(true);
    } catch (error: any) {
      const err = error?.response?.data?.error;
      if (err) {
        setPhoneError('Telefone já cadastrado.');
      }
    } finally {
      setIsLoadingInfo(false);
    }
  }

  async function validateName() {
    if (name.length < 5) {
      setNameError('Digite o seu nome completo');
    }
  }

  async function validatePassword() {
    if (password.length < 4) {
      setPasswordError('Mínimo de 4 caracteres.');
    }
  }

  function resetForm() {
    setCpf('');
    setCpfError('');
    setCpfIsValid(false);
    setEmail('');
    setEmailError('');
    setEmailIsValid(false);
    setAceitaReceberPromos(false);
    setName('');
    setNameError('');
    setBirthEnabled(false);
    setPhone('');
    setPhoneRaw('');
    setPhoneError('');
    setPhoneIsValid(false);
    setPassword('');
    setPasswordError('');
    setFormStep('info');
    setTermAccepted(false);
    setCode('');
    setCodeError('');
  }

  const [buttonPressed, setButtonPressed] = useState(false);

  async function onSubmit() {
    if (!emailIsValid) {
      const isValid = await validateEmail();
      if (!isValid) {
        setIsLoadingIndica(false);
        return;
      }
    }

    const capiName = name
      .toLowerCase()
      .replace(/(?:^|\s)\S/g, function (capitalize) {
        return capitalize.toUpperCase();
      });

    const user = {
      name: capiName,
      cpf,
      email,
      phone,
      password,
      birth_date: birth,
      codIndicacao: code,
      clientId,
      aceitaReceberPromos,
    };

    try {
      setButtonPressed(true);
      setIsLoadingIndica(true);
      const response = await api.post('/users', user);

      if (response) {
        try {
          const instalacao = await getInstallAttributionData();
          const res = await api.post('/login_b', {
            cpf,
            password: stringToHex(password),
            preLogin: true,
            clientId,
            instalacao,
          });

          const { data } = res;
          await signIn({ token: data.token, user: data.user });
          setShouldShowOTPModal(true);
          setShowSign(false);
          onSuccess();
          resetForm();
          setIsLoadingAuth(false);
        } catch (error) {
          Alert.alert(
            'Não foi possível realizar o login automaticamente',
            'Tente fazer login em sua conta. Caso contrário, entre em contato com o suporte.',
          );
        }
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const rawError =
        typeof data?.error === 'string'
          ? data.error
          : typeof data?.errors === 'string'
            ? data.errors
            : typeof data?.message === 'string'
              ? data.message
              : '';

      let message =
        'Não foi possível criar sua conta. Verifique seus dados e tente novamente.';

      if (status === 422) {
        message = 'Verifique o e-mail e tente novamente.';
        setFormStep('auth');
        setEmailError('E-mail incorreto');
        setEmailIsValid(false);
      }

      if (rawError && !rawError.toLowerCase().includes('translation missing')) {
        message = rawError;
      }

      Alert.alert('Não foi possível criar sua conta', message);
    } finally {
      setIsLoadingIndica(false);
      setButtonPressed(false);
    }
  }

  function handleOnBack() {
    if (formStep === 'info') {
      resetForm();
      return onBack();
    }

    if (formStep === 'indica') {
      return setFormStep('auth');
    }
    setFormStep('info');
  }

  useEffect(() => {
    if (cpf.length === 11) {
      validateCpf();
    }
  }, [cpf]);

  useEffect(() => {
    if (phoneRaw.length === 11) {
      validatePhone();
    }
  }, [phoneRaw]);

  async function validateCode() {
    setIsLoadingIndica(true);
    await api
      .post('/verifyCodCompartilhamento', {
        codIndicacao: code,
      })
      .then(() => {
        onSubmit();
      })
      .catch(({ response }) => {
        setIsLoadingIndica(false);
        if (response.data.msg) {
          return setCodeError(response.data.msg);
        }
        return setCodeError(
          'Não foi possível inserir o código de indicação, tente novamente mais tarde.',
        );
      });
  }

  function handleValidateWithCode() {
    if (code.length > 0) {
      validateCode();
    } else {
      onSubmit();
    }
  }

  return (
    <>
      <BackgroundHeader>
        <IconButton onPress={handleOnBack}>
          <IconHeader name="chevron-left" />
        </IconButton>
      </BackgroundHeader>
      <Form>
         <FormHeader>
                 <FormInfos>
                   <Title>Criar conta</Title>
                   <Subtitle>Crie sua conta e comece a economizar</Subtitle>
                 </FormInfos>

               </FormHeader>

        {formStep === 'info' && (
          <FormStep>
            <Step>
              <LineStepSeparator active width="50px" />
              <CircleStep status="editing">
                <StepText active={true} width={78}>
                  Seus dados
                </StepText>
              </CircleStep>
              <LineStep active={false} />
              <CircleStep status="pending">
                <StepText active={false} width={75}>
                  Seu acesso
                </StepText>
              </CircleStep>
              <LineStep active={false} />
              <CircleStep status="pending">
                <StepText active={false} width={75}>
                  Indicação
                </StepText>
              </CircleStep>
              <LineStepSeparator active={false} width="50px" />
            </Step>
            <FormControl>
              <Label>Nome completo</Label>
              <AuthInput
                errorStyle={!!nameError}
                error={nameError}
                keyboardType="default"
                placeholder="Digite seu nome completo"
                onChangeText={handleNameChangeText}
                value={name}
                onBlur={validateName}
              />
            </FormControl>

            <FormControl>
              <Label>Telefone</Label>
              <AuthInput
                errorStyle={!!phoneError}
                error={phoneError}
                placeholder="(99) 99999-9999"
                autoCapitalize="none"
                keyboardType="number-pad"
                onChangeText={handlePhoneChangeText}
                value={phone}
                mask={'([00]) [00000]-[0000]'}
              />
            </FormControl>

            <BirthInput enabled={setBirthEnabled} onChangeDate={setBirth} />
          </FormStep>
        )}
        {formStep === 'auth' && (
          <FormStep>
            <Step>
              <LineStepSeparator active width="50px" />
              <CircleStep status="done">
                <StepText active={true} width={78}>
                  Seus dados
                </StepText>
              </CircleStep>
              <LineStep active />
              <CircleStep status="editing">
                <StepText active={false} width={75}>
                  Seu acesso
                </StepText>
              </CircleStep>
              <LineStep active={false} />
              <CircleStep status="pending">
                <StepText active={false} width={75}>
                  Indicação
                </StepText>
              </CircleStep>
              <LineStepSeparator active={false} width="50px" />
            </Step>
            <FormControl>
              <Label>Seu CPF</Label>
              <AuthInput
                errorStyle={!!cpfError}
                error={cpfError}
                placeholder="000.000.000-00"
                keyboardType="number-pad"
                onChangeText={(_, extracted: any) => {
                  return handleCpfChangeText(extracted);
                }}
                value={cpf}
                mask={'[000].[000].[000]-[00]'}
              />
            </FormControl>
            <FormControl>
              <Label>Seu e-mail</Label>
              <AuthInput
                errorStyle={!!emailError}
                error={emailError}
                placeholder="email@exemplo.com"
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                textContentType="emailAddress"
                importantForAutofill="yes"
                onChangeText={e => handleEmailChangeText(e)}
                value={email}
                onBlur={validateEmail}
              />
            </FormControl>
            <FormControl>
              <Label>Nova senha</Label>
              <AuthInput
                errorStyle={!!passwordError}
                error={passwordError}
                placeholder="* * * * * *"
                keyboardType="default"
                autoCapitalize="none"
                onChangeText={handlePasswordChangeText}
                passwordInput
                value={password}
                onBlur={validatePassword}
              />
            </FormControl>
            <PromosContainer>
              <PromoCheckButton
                onPress={() => setAceitaReceberPromos(!aceitaReceberPromos)}
                activeOpacity={1}>
                <PromoCheckIcon
                  name={aceitaReceberPromos ? 'check-square' : 'square-o'}
                  checked={aceitaReceberPromos}
                />
                <PromoLabel>
                  Quero receber promoções por Whatsapp, SMS e E-mail
                </PromoLabel>
              </PromoCheckButton>
            </PromosContainer>
          </FormStep>
        )}

        {formStep === 'indica' && (
          <FormStep>
            <Step>
              <LineStepSeparator active width="50px" />
              <CircleStep status="done">
                <StepText active={true} width={78}>
                  Seus dados
                </StepText>
              </CircleStep>
              <LineStep active />
              <CircleStep status="done">
                <StepText active={true} width={75}>
                  Seu acesso
                </StepText>
              </CircleStep>
              <LineStep active={false} />
              <CircleStep status="editing">
                <StepText active={false} width={75}>
                  Indicação
                </StepText>
              </CircleStep>
              <LineStepSeparator active={false} width="50px" />
            </Step>
            <FormControl>
              <Label>Possui código de indicação do Indique e Ganhe? (opcional)</Label>
              <AuthInput
                errorStyle={!!codeError}
                error={codeError}
                placeholder="0000000000"
                keyboardType="default"
                autoCapitalize="none"
                onChangeText={handlCodeChangeText}
                value={code}
              />
            </FormControl>

            <TermContainerBox>
              <Label>Aceite os termos abaixo:</Label>
              <TermContainer>
                <TermLinkButton
                  activeOpacity={1}
                  onPress={() =>
                    Linking.openURL(
                      'https://barataocombustiveis.com.br/termos-e-condicoes',
                    )
                  }>
                  <TermLinkText>Termos e condições</TermLinkText>
                </TermLinkButton>

                <TermCheckButton
                  onPress={() => setTermAccepted(!termAccepted)}
                  activeOpacity={1}>
                  <TermCheckIcon
                    name={termAccepted ? 'check-square' : 'square-o'}
                    checked={termAccepted}
                  />
                  <Label>Eu aceito</Label>
                </TermCheckButton>
              </TermContainer>
            </TermContainerBox>
          </FormStep>
        )}
      </Form>
      <Footer>
        {formStep === 'info' && (
          <Button
            title="Continuar"
            onPress={() => setFormStep('auth')}
            enabled={isInfoEnabled || isLoadingInfo}
            loading={isLoadingInfo}
          />
        )}
        {formStep === 'auth' && (
          <Button
            title="Continuar"
            onPress={() => setFormStep('indica')}
            enabled={isAuthEnabled || isLoadingAuth}
            loading={isLoadingAuth}
          />
        )}
        {formStep === 'indica' && (
          <Button
            title="Criar minha conta"
            onPress={handleValidateWithCode}
            enabled={(isIndicaEnabled || isLoadingIndica) && !buttonPressed}
            loading={isLoadingIndica}
          />
        )}
      </Footer>
    </>
  );
}
